import * as THREE from 'three';

export interface ViewCubeTargetData {
  type: 'face' | 'edge' | 'corner';
  name: string;
  label: string;
  viewDir: THREE.Vector3;
}

/** Pointer hit-testing and hover feedback for the CAD view cube. */
export class ViewCubeInteraction {
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private hovered: THREE.Mesh | null = null;
  private readonly camera: THREE.Camera;
  private readonly targets: THREE.Mesh[];
  private readonly canvas: HTMLElement;
  private readonly viewport: HTMLElement;
  private readonly highlight: THREE.Mesh;
  private readonly size: number;
  private readonly topOffset: number;

  constructor(
    camera: THREE.Camera,
    targets: THREE.Mesh[],
    canvas: HTMLElement,
    viewport: HTMLElement,
    highlight: THREE.Mesh,
    size = 100,
    topOffset = 50,
  ) {
    this.camera = camera;
    this.targets = targets;
    this.canvas = canvas;
    this.viewport = viewport;
    this.highlight = highlight;
    this.size = size;
    this.topOffset = topOffset;
  }

  public getHit(clientX: number, clientY: number): THREE.Intersection | null {
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const left = Math.round((this.viewport.clientWidth - this.size) / 2);
    if (x < left || x > left + this.size || y < this.topOffset || y > this.topOffset + this.size) return null;

    this.pointer.set((x - left) / this.size * 2 - 1, -((y - this.topOffset) / this.size * 2 - 1));
    this.raycaster.setFromCamera(this.pointer, this.camera);
    return this.raycaster.intersectObjects(this.targets, false)[0] ?? null;
  }

  /** Capture ViewCube clicks before the canvas controls can interpret them as orbit gestures. */
  public bindClick(onSelect: (target: ViewCubeTargetData) => void): () => void {
    let pointerDownTarget: THREE.Mesh | null = null;
    let pointerDownPosition = { x: 0, y: 0 };
    const onPointerDown = (event: PointerEvent) => {
      const hit = this.getHit(event.clientX, event.clientY);
      if (!hit) return;
      pointerDownTarget = hit.object as THREE.Mesh;
      pointerDownPosition = { x: event.clientX, y: event.clientY };
      event.stopImmediatePropagation();
    };
    const onPointerUp = (event: PointerEvent) => {
      if (!pointerDownTarget) return;
      const target = pointerDownTarget;
      pointerDownTarget = null;
      event.stopImmediatePropagation();
      const distance = Math.hypot(event.clientX - pointerDownPosition.x, event.clientY - pointerDownPosition.y);
      if (distance > 6) return;
      const hit = this.getHit(event.clientX, event.clientY);
      if (hit?.object === target && target.userData?.viewDir) {
        onSelect(target.userData as ViewCubeTargetData);
      }
    };
    this.canvas.addEventListener('pointerdown', onPointerDown, { capture: true });
    this.canvas.addEventListener('pointerup', onPointerUp, { capture: true });
    return () => {
      this.canvas.removeEventListener('pointerdown', onPointerDown, { capture: true });
      this.canvas.removeEventListener('pointerup', onPointerUp, { capture: true });
    };
  }

  public updateHover(clientX: number, clientY: number): boolean {
    const hit = this.getHit(clientX, clientY);
    if (!hit) {
      this.clearHover();
      return false;
    }
    const target = hit.object as THREE.Mesh;
    if (this.hovered !== target) {
      this.hovered = target;
      this.highlight.geometry = target.geometry;
      this.highlight.position.copy(target.position);
      this.highlight.rotation.copy(target.rotation);
      this.highlight.scale.copy(target.scale);
      this.highlight.visible = true;
    }
    this.canvas.style.cursor = 'pointer';
    return true;
  }

  public clearHover(): void {
    if (!this.hovered) return;
    this.hovered = null;
    this.highlight.visible = false;
    if (this.canvas.style.cursor === 'pointer') this.canvas.style.cursor = 'default';
  }
}


/** Build and own the CAD view cube scene and its hit-testing interaction. */
export function createViewCube(renderer: THREE.WebGLRenderer, viewport: HTMLElement) {
  // ---- Interactive 3D ViewCube (CAD Navigation Cube) ----

const gizmoScene = new THREE.Scene();
const frustumHalf = 0.88;
const gizmoCamera = new THREE.OrthographicCamera(-frustumHalf, frustumHalf, frustumHalf, -frustumHalf, 0.1, 50);
gizmoCamera.position.set(3, 3, 3);
gizmoCamera.lookAt(0, 0, 0);

const viewCubeTargets: THREE.Mesh[] = [];

// Solid white core cube
const coreCubeSize = 0.62;
const coreCubeGeo = new THREE.BoxGeometry(coreCubeSize, coreCubeSize, coreCubeSize);
const coreCubeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
const coreCubeMesh = new THREE.Mesh(coreCubeGeo, coreCubeMat);
gizmoScene.add(coreCubeMesh);

// Subtle border lines on cube
const cubeWireEdges = new THREE.LineSegments(
  new THREE.EdgesGeometry(coreCubeGeo),
  new THREE.LineBasicMaterial({ color: 0xcbd5e1, transparent: true, opacity: 0.8 })
);
gizmoScene.add(cubeWireEdges);

// Face textures generator: white background, crisp dark slate font, inset outline
const createCubeFaceTexture = (text: string): THREE.CanvasTexture => {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Clean white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);

  // Subtle inset border
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 6;
  ctx.strokeRect(3, 3, size - 6, size - 6);

  // Text
  ctx.fillStyle = '#1e293b';
  ctx.font = '600 48px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, size / 2, size / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

// Dedicated hover highlight mesh for ViewCube
const hoverHighlightMat = new THREE.MeshBasicMaterial({
  color: 0x3b82f6,
  transparent: true,
  opacity: 0.45,
  depthTest: false,
  side: THREE.DoubleSide
});
const hoverHighlightMesh = new THREE.Mesh(new THREE.BufferGeometry(), hoverHighlightMat);
hoverHighlightMesh.renderOrder = 998;
hoverHighlightMesh.visible = false;
gizmoScene.add(hoverHighlightMesh);

// 1. Build 6 Faces
const facePadSize = 0.46;
const facePlaneGeo = new THREE.PlaneGeometry(facePadSize, facePadSize);
const faceOffset = 0.311;

interface FaceDef {
  name: string;
  text: string;
  pos: THREE.Vector3;
  normal: THREE.Vector3;
  up: THREE.Vector3;
  viewDir: THREE.Vector3;
}

const faces: FaceDef[] = [
  { name: 'Front',  text: 'Front',  pos: new THREE.Vector3(0, -faceOffset, 0), normal: new THREE.Vector3(0, -1, 0), up: new THREE.Vector3(0, 0, 1), viewDir: new THREE.Vector3(0, -1, 0) },
  { name: 'Back',   text: 'Back',   pos: new THREE.Vector3(0, faceOffset, 0),  normal: new THREE.Vector3(0, 1, 0),  up: new THREE.Vector3(0, 0, 1), viewDir: new THREE.Vector3(0, 1, 0) },
  { name: 'Left',   text: 'Left',   pos: new THREE.Vector3(-faceOffset, 0, 0), normal: new THREE.Vector3(-1, 0, 0), up: new THREE.Vector3(0, 0, 1), viewDir: new THREE.Vector3(-1, 0, 0) },
  { name: 'Right',  text: 'Right',  pos: new THREE.Vector3(faceOffset, 0, 0),  normal: new THREE.Vector3(1, 0, 0),  up: new THREE.Vector3(0, 0, 1), viewDir: new THREE.Vector3(1, 0, 0) },
  { name: 'Top',    text: 'Top',    pos: new THREE.Vector3(0, 0, faceOffset),  normal: new THREE.Vector3(0, 0, 1),  up: new THREE.Vector3(0, 1, 0), viewDir: new THREE.Vector3(0, 0.0001, 1) },
  { name: 'Bottom', text: 'Bottom', pos: new THREE.Vector3(0, 0, -faceOffset), normal: new THREE.Vector3(0, 0, -1), up: new THREE.Vector3(0, -1, 0), viewDir: new THREE.Vector3(0, -0.0001, -1) }
];

faces.forEach((f) => {
  const mat = new THREE.MeshBasicMaterial({ map: createCubeFaceTexture(f.text), side: THREE.FrontSide });
  const mesh = new THREE.Mesh(facePlaneGeo, mat);
  mesh.position.copy(f.pos);
  mesh.up.copy(f.up);
  mesh.lookAt(f.pos.clone().add(f.normal));
  mesh.userData = {
    type: 'face',
    name: f.name,
    label: `${f.name} View`,
    viewDir: f.viewDir
  } as ViewCubeTargetData;
  gizmoScene.add(mesh);
  viewCubeTargets.push(mesh);
});

// 2. Build 12 Edges
const edgeGeoX = new THREE.BoxGeometry(0.46, 0.08, 0.08);
const edgeGeoY = new THREE.BoxGeometry(0.08, 0.46, 0.08);
const edgeGeoZ = new THREE.BoxGeometry(0.08, 0.08, 0.46);
const edgeMat = new THREE.MeshBasicMaterial({ color: 0xf1f5f9, transparent: true, opacity: 0.95 });

interface EdgeDef {
  name: string;
  geo: THREE.BufferGeometry;
  pos: THREE.Vector3;
  viewDir: THREE.Vector3;
}

const c1 = 0.27; // center of edge strip
const edges: EdgeDef[] = [
  // 4 X-edges
  { name: 'Top-Front',    geo: edgeGeoX, pos: new THREE.Vector3(0, -c1, c1),  viewDir: new THREE.Vector3(0, -1, 1).normalize() },
  { name: 'Top-Back',     geo: edgeGeoX, pos: new THREE.Vector3(0, c1, c1),   viewDir: new THREE.Vector3(0, 1, 1).normalize() },
  { name: 'Bottom-Front', geo: edgeGeoX, pos: new THREE.Vector3(0, -c1, -c1), viewDir: new THREE.Vector3(0, -1, -1).normalize() },
  { name: 'Bottom-Back',  geo: edgeGeoX, pos: new THREE.Vector3(0, c1, -c1),  viewDir: new THREE.Vector3(0, 1, -1).normalize() },
  // 4 Y-edges
  { name: 'Top-Left',     geo: edgeGeoY, pos: new THREE.Vector3(-c1, 0, c1),  viewDir: new THREE.Vector3(-1, 0, 1).normalize() },
  { name: 'Top-Right',    geo: edgeGeoY, pos: new THREE.Vector3(c1, 0, c1),   viewDir: new THREE.Vector3(1, 0, 1).normalize() },
  { name: 'Bottom-Left',  geo: edgeGeoY, pos: new THREE.Vector3(-c1, 0, -c1), viewDir: new THREE.Vector3(-1, 0, -1).normalize() },
  { name: 'Bottom-Right', geo: edgeGeoY, pos: new THREE.Vector3(c1, 0, -c1),  viewDir: new THREE.Vector3(1, 0, -1).normalize() },
  // 4 Z-edges
  { name: 'Front-Left',   geo: edgeGeoZ, pos: new THREE.Vector3(-c1, -c1, 0), viewDir: new THREE.Vector3(-1, -1, 0).normalize() },
  { name: 'Front-Right',  geo: edgeGeoZ, pos: new THREE.Vector3(c1, -c1, 0),  viewDir: new THREE.Vector3(1, -1, 0).normalize() },
  { name: 'Back-Left',    geo: edgeGeoZ, pos: new THREE.Vector3(-c1, c1, 0),  viewDir: new THREE.Vector3(-1, 1, 0).normalize() },
  { name: 'Back-Right',   geo: edgeGeoZ, pos: new THREE.Vector3(c1, c1, 0),   viewDir: new THREE.Vector3(1, 1, 0).normalize() }
];

edges.forEach((e) => {
  const mesh = new THREE.Mesh(e.geo, edgeMat);
  mesh.position.copy(e.pos);
  mesh.userData = {
    type: 'edge',
    name: e.name,
    label: `${e.name} Edge View`,
    viewDir: e.viewDir
  } as ViewCubeTargetData;
  gizmoScene.add(mesh);
  viewCubeTargets.push(mesh);
});

// 3. Build 8 Corners
const cornerGeo = new THREE.BoxGeometry(0.08, 0.08, 0.08);
const cornerMat = new THREE.MeshBasicMaterial({ color: 0xe2e8f0, transparent: true, opacity: 0.95 });

const corners: { name: string; pos: THREE.Vector3; viewDir: THREE.Vector3 }[] = [
  { name: 'Top-Front-Left',     pos: new THREE.Vector3(-c1, -c1, c1),  viewDir: new THREE.Vector3(-1, -1, 1).normalize() },
  { name: 'Top-Front-Right',    pos: new THREE.Vector3(c1, -c1, c1),   viewDir: new THREE.Vector3(1, -1, 1).normalize() },
  { name: 'Top-Back-Left',      pos: new THREE.Vector3(-c1, c1, c1),   viewDir: new THREE.Vector3(-1, 1, 1).normalize() },
  { name: 'Top-Back-Right',     pos: new THREE.Vector3(c1, c1, c1),    viewDir: new THREE.Vector3(1, 1, 1).normalize() },
  { name: 'Bottom-Front-Left',  pos: new THREE.Vector3(-c1, -c1, -c1), viewDir: new THREE.Vector3(-1, -1, -1).normalize() },
  { name: 'Bottom-Front-Right', pos: new THREE.Vector3(c1, -c1, -c1),  viewDir: new THREE.Vector3(1, -1, -1).normalize() },
  { name: 'Bottom-Back-Left',   pos: new THREE.Vector3(-c1, c1, -c1),  viewDir: new THREE.Vector3(-1, 1, -1).normalize() },
  { name: 'Bottom-Back-Right',  pos: new THREE.Vector3(c1, c1, -c1),   viewDir: new THREE.Vector3(1, 1, -1).normalize() }
];

corners.forEach((c) => {
  const mesh = new THREE.Mesh(cornerGeo, cornerMat);
  mesh.position.copy(c.pos);
  mesh.userData = {
    type: 'corner',
    name: c.name,
    label: `${c.name} Corner View`,
    viewDir: c.viewDir
  } as ViewCubeTargetData;
  gizmoScene.add(mesh);
  viewCubeTargets.push(mesh);
});

// 4. Build 3 Axes (X red, Y green, Z blue) with letter labels
const makeAxisLabel = (text: string, color: string): THREE.Sprite => {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.font = 'bold 36px ui-monospace, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  ctx.fillText(text, size / 2, size / 2);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  sprite.scale.set(0.24, 0.24, 0.24);
  sprite.renderOrder = 1000;
  return sprite;
};

const createAxisLine = (p1: THREE.Vector3, p2: THREE.Vector3, color: number): THREE.Mesh => {
  const dir = new THREE.Vector3().subVectors(p2, p1);
  const len = dir.length();
  const geo = new THREE.CylinderGeometry(0.009, 0.009, len, 12);
  const mat = new THREE.MeshBasicMaterial({ color, depthTest: false });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 999;
  mesh.position.copy(p1).addScaledVector(dir, 0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  return mesh;
};

const axisOrig = new THREE.Vector3(-0.31, -0.31, -0.31);
const axisEndX = new THREE.Vector3(0.48, -0.31, -0.31);
const axisEndY = new THREE.Vector3(-0.31, 0.48, -0.31);
const axisEndZ = new THREE.Vector3(-0.31, -0.31, 0.48);

gizmoScene.add(createAxisLine(axisOrig, axisEndX, 0xef4444));
gizmoScene.add(createAxisLine(axisOrig, axisEndY, 0x22c55e));
gizmoScene.add(createAxisLine(axisOrig, axisEndZ, 0x3b82f6));

const labelX = makeAxisLabel('X', '#ef4444'); labelX.position.set(0.55, -0.31, -0.31);
const labelY = makeAxisLabel('Y', '#22c55e'); labelY.position.set(-0.31, 0.55, -0.31);
const labelZ = makeAxisLabel('Z', '#3b82f6'); labelZ.position.set(-0.31, -0.31, 0.55);
gizmoScene.add(labelX, labelY, labelZ);

const viewCubeInteraction = new ViewCubeInteraction(
  gizmoCamera,
  viewCubeTargets,
  renderer.domElement,
  viewport,
  hoverHighlightMesh,
);


  return { scene: gizmoScene, camera: gizmoCamera, interaction: viewCubeInteraction };
}

